import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { CustomerInterface } from '@contracts/interfaces/customer.interface';
import { CustomerDialogService } from '../../dialogs/customer/customer.service.dialog';
import { CustomerImportDialogService } from '../../dialogs/customer-import/customer-import.service.dialog';
import { CustomerService } from '../../services/customer.service';
import { validate_phone_number } from '../../tools/phone-number';

@Component({
  selector: 'app-customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.scss']
})
export class CustomersComponent implements OnInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  dataSource: MatTableDataSource<CustomerInterface>;
  columnsToDisplay = [
    'name',
    'phone',
    'email',
    'googleReview',
    'remoteID'
  ];

  customers: CustomerInterface[] = []

  searchText: string = "";

  count: number = 0;
  start: number = 0;
  pageSize: number = 15;
  pageIndex: number = 0;
  private rowLoading = new Set<string>();
  private rowLocked = new Set<string>();
  alphabetFilters: string[] = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z','0-9','All'];
  activeAlpha: string = 'A';

  constructor(
    private cdr: ChangeDetectorRef,
    public customerDialogService: CustomerDialogService,
    public customerImportDialogService: CustomerImportDialogService,
    public customerService: CustomerService) { }

  validate_phone_number = validate_phone_number;
    
  ngOnInit(): void {
    this.reload();
  }

  reload() {
    this.customerService.find({}, null, null).subscribe((customers: any) => {
      this.customers = customers;
      this.applyAlphaFilter();
    })
  }

  pageChange(evt: PageEvent) {

  }

  importDialog() {
    this.customerImportDialogService.open(r => {
      this.reload();
    });
  }

  exportGoogleContacts() {
    const customers = (this.dataSource?.data?.length ? this.dataSource.data : this.customers) || [];
    if (!customers.length) {
      return;
    }

    const headers = [
      'Name',
      'Given Name',
      'Phone 1 - Value',
      'Phone 2 - Value',
      'E-mail 1 - Value',
      'Notes'
    ];

    const escape = (value: string | undefined | null) => {
      const safe = (value || '').toString().replace(/"/g, '""');
      return `"${safe}"`;
    };

    const rows = customers.map(customer => [
      escape(customer.name),
      escape(customer.nick),
      escape(customer.phone),
      escape(customer.phone2),
      escape(customer.email),
      escape(customer.notes)
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'customers-google-contacts.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  showCustomerDialog(customer) {
    const isRowClick = !!customer;
    const key = isRowClick ? this.getCustomerKey(customer) : null;

    if (isRowClick && key && this.rowLocked.has(key)) {
      return;
    }

    if (isRowClick && key) {
      this.rowLocked.add(key);
      this.rowLoading.add(key);
      this.cdr.detectChanges();
    }

    this.customerDialogService.open(
      customer,
      null,
      r=> {
        if (isRowClick && key) {
          this.rowLoading.delete(key);
          this.rowLocked.delete(key);
          this.cdr.detectChanges();
        }
        this.handleDialogResult(r, customer);
      },
      (dialogRef) => {
        if (isRowClick && key) {
          this.rowLoading.delete(key);
          this.cdr.detectChanges();
        }
      }
    );
  }

  private handleDialogResult(result: any, originalCustomer?: CustomerInterface) {
    if (!this.dataSource) {
      this.reload();
      return;
    }

    // Cancel or closed without action
    if (!result || result?.action === 'cancel') {
      // Remove deleted customer if dialog returned null and we had an original
      if (result === null && originalCustomer) {
        this.removeCustomerFromList(originalCustomer);
      }
      return;
    }

    // result is a customer object (create/update)
    const updatedCustomer = result as CustomerInterface;
    this.upsertCustomerInList(updatedCustomer);
  }

  private upsertCustomerInList(customer: CustomerInterface) {
    const key = this.getCustomerKey(customer);
    const data = [...(this.customers || [])];
    const idx = data.findIndex(c => this.getCustomerKey(c) === key);
    if (idx >= 0) {
      data[idx] = customer;
    } else {
      data.unshift(customer);
    }
    this.customers = data;
    this.applyAlphaFilter();
  }

  private removeCustomerFromList(customer: CustomerInterface) {
    const key = this.getCustomerKey(customer);
    this.customers = (this.customers || []).filter(c => this.getCustomerKey(c) !== key);
    this.applyAlphaFilter();
  }

  searchChange(query: string) {
    this.customerService.search(query, 0).subscribe((customers: any) => {
      this.customers = customers;
      this.applyAlphaFilter();
    })
  }

  stopSearching() {
    this.customerService.search("", 0).subscribe((customers: any) => {
      this.customers = customers;
      this.applyAlphaFilter();
    });
  }

  isRowLoading(customer: CustomerInterface): boolean {
    const key = this.getCustomerKey(customer);
    return this.rowLoading.has(key);
  }

  getReviewLabel(status: CustomerInterface['googleReviewStatus']): string {
    switch (status) {
      case 'requested':
        return 'Review request sent';
      case 'left':
        return 'Review left';
      case 'do_not_request':
        return 'Do not request';
      default:
        return 'No review request sent';
    }
  }

  private getCustomerKey(customer: CustomerInterface): string {
    return (customer as any)?._id || (customer as any)?.remoteID || (customer as any)?.email || (customer as any)?.name || '';
  }

  selectAlpha(letter: string) {
    this.activeAlpha = letter;
    this.applyAlphaFilter();
  }

  private applyAlphaFilter() {
    const customers = this.customers || [];
    let filtered = customers;

    if (this.activeAlpha && this.activeAlpha !== 'All') {
      if (this.activeAlpha === '0-9') {
        filtered = customers.filter(c => /^\d/.test((c.name || '').trim()));
      } else {
        const letter = this.activeAlpha.toUpperCase();
        filtered = customers.filter(c => (c.name || '').trim().toUpperCase().startsWith(letter));
      }
    }

    this.dataSource = new MatTableDataSource<CustomerInterface>(filtered);
    this.cdr.detectChanges();
  }

}
